// Shared helpers for building external-site URLs from a Property.
//
// Why this lives in a shared file: PropertyDetail.tsx used to build its own
// buildZillowUrl(address) helper that ignored property type (as did the now-
// deleted PropertyPanel.tsx). That meant a vacant lot or commercial parcel
// got a direct Zillow homes URL that 404s (Zillow only indexes residential
// listings). Bad UX: user clicks "View on Zillow", lands on an error or
// unrelated property, loses trust in the data.
//
// This helper consolidates the logic + adds a vacant-land check that falls
// back to a Zillow search URL (which always renders, even with no results).

import { Property } from "../types";
import { isSyntheticParcelId } from "./dataQuality";

/**
 * isVacantLand reports whether a property has no structure on it.
 *
 * Checked first — positive structure evidence (beds/baths, or OneMap's
 * explicit hasStructure flag) short-circuits to false before any of the
 * three vacancy signals below run. See the guard's own comment for why.
 *
 * Three signals (any one is sufficient):
 *   1. building.structureCount === 0 — the source reports zero buildings.
 *      Polaris returns an explicit empty array for vacant parcels (post-PR
 *      #102 multi-building work), which the server surfaces as a real 0.
 *      `undefined` means the source does not publish a building list at all
 *      and is NOT a vacancy signal — hence the `=== 0`, not a falsy check.
 *   2. building.totalSqft === 0 — fallback for sources without a building list.
 *   3. building.landUseDesc contains a vacant-land keyword (Mecklenburg's
 *      assessor uses "VACANT", "AGRICULTURAL", "PARK", "INDUSTRIAL VACANT",
 *      etc. — match conservatively to avoid hiding Zillow on actual homes).
 *
 * Reads the typed `building` group rather than raw_data (#1872): the server
 * has already done the string-vs-number coercion, so a source that writes
 * "0" instead of 0 now reaches signal 2 as well. That is a deliberate
 * widening — a string zero was always meant to count, and the old
 * `typeof === "number"` guard silently dropped it.
 *
 * Defensive: returns false (treats as residential, shows Zillow homes URL)
 * when the property has no CAMA enrichment. Worst case: Zillow link 404s on a
 * fringe parcel. Best case: most users see a working link.
 */
export function isVacantLand(property: Property): boolean {
  const building = property.building;
  if (!building) return false;

  // Positive structure evidence overrides every signal below — a parcel
  // with recorded beds/baths or OneMap's explicit "has structure" flag is
  // not vacant land no matter what land_use_desc/structureCount/totalSqft
  // claim. Found live 2026-09-18: 93 properties statewide were flagged
  // vacant this way despite carrying real bed/bath data — most commonly a
  // large-acreage parcel whose county land-use code is "Forestry"/
  // "Agricultural"/"Developmt Restricted" at the PARCEL level even though a
  // residence sits on it (verified against live NC OneMap for a Wake County
  // sample: struct='Y', structyear=1995, on a 21-acre "Forestry" parcel).
  // classify.go's isLandOnly() has the identical gap on the backend
  // property_class column (#2253/#2264 fixed the "stale verdict" case but
  // deliberately left the keyword branch supreme — see issue filed for
  // that side) — this fixes only the frontend signal this file owns.
  if (
    building.hasStructure === true ||
    (building.bedrooms ?? 0) > 0 ||
    (building.fullBaths ?? 0) > 0 ||
    (building.halfBaths ?? 0) > 0
  ) {
    return false;
  }

  // Signal 1: the source explicitly reports zero buildings.
  if (building.structureCount === 0) return true;

  // Signal 2: explicit zero sqft
  if (building.totalSqft === 0) return true;

  // Signal 3: land_use_desc keyword match
  const landUse = building.landUseDesc;
  if (landUse) {
    const upper = landUse.toUpperCase();
    // The 9 entries from HOA through FORREST LAND were added 2026-09-08 —
    // keep in exact sync with backend/internal/propertyclass/classify.go's
    // vacantLandKeywords; classify_test.go's symmetry test asserts this
    // array matches that Go table element-for-element. See that file's
    // comment for why FORREST LAND is its own entry (vendor misspelling,
    // does not match FOREST) and why ACRE WITH IMPROVEMENT is a prefix, not
    // the full vendor string (comma placement varies by source).
    const vacantKeywords = [
      "VACANT",
      "AGRICULTURAL",
      "AGRICULTURE",
      "PARK",
      "FOREST",
      "PASTURE",
      "WETLAND",
      "RIGHT OF WAY",
      "RIGHT-OF-WAY",
      "HOA",
      "CONDO COMPLEX",
      "LOT VALUE",
      "ACRE WITH IMPROVEMENT",
      "WATER/SEWER SYSTEM",
      "COMMON AREA",
      "DEVELOPMT RESTRICTED",
      "BLDG SITE",
      "FORREST LAND",
    ];
    for (const kw of vacantKeywords) {
      if (upper.includes(kw)) return true;
    }
  }

  return false;
}

/**
 * buildZillowUrl returns a Zillow URL for a property's address.
 *
 * For non-vacant properties: returns the canonical /homes/{slug}_rb/ URL,
 * which deep-links to the property's Zillow listing if it exists.
 *
 * For vacant land: returns the Zillow search URL with the address as the
 * query, which always renders (even if no results match). This avoids the
 * 404/wrong-property bug where Zillow's homes URL silently fails for
 * non-residential parcels.
 *
 * Falls back to search URL if address is empty/blank.
 */
export function buildZillowUrl(property: Property): string {
  const address = property.address?.trim() ?? "";
  if (!address) {
    // No address — search URL with city + state as best-effort
    const fallback = [property.city, property.state].filter(Boolean).join(", ");
    return `https://www.zillow.com/homes/?searchQueryState=${encodeURIComponent(
      JSON.stringify({ usersSearchTerm: fallback || "North Carolina" }),
    )}`;
  }

  // Always locality-qualify the address with city/state/zip. A bare street
  // name is ambiguous nationwide — "Waterfowl Ln" resolved to Chardon, OH
  // instead of Charlotte, NC. Appending the locality pins Zillow to the
  // right place for both the search term and the /homes slug.
  const qualified = qualifyAddress(property);

  if (isVacantLand(property)) {
    // Search URL — works even when Zillow has no listing for the parcel
    return `https://www.zillow.com/homes/?searchQueryState=${encodeURIComponent(
      JSON.stringify({ usersSearchTerm: qualified }),
    )}`;
  }

  // Direct homes URL for residential properties
  const slug = qualified
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "-");
  return `https://www.zillow.com/homes/${slug}_rb/`;
}

/**
 * qualifyAddress joins the street address with city, state, and zip so an
 * address-based lookup resolves in the correct locality. Empty parts are
 * dropped; a property with only a street address still returns just that.
 */
function qualifyAddress(property: Property): string {
  // Prefer a point-in-polygon-derived NG911 situs (NC OneMap AddressNC,
  // surfaced as the typed `situs` group) over properties.address, which for
  // legal-description-only counties (Cabarrus) is the deed text — building a
  // Zillow URL from "LT 28 KENSINGTON FOREST" would be garbage. The derived
  // situs carries its own city/zip.
  const situs = property.situs;
  const derivedStreet = situs?.address ?? "";
  if (derivedStreet) {
    return [derivedStreet, situs?.city ?? "", property.state, situs?.zip ?? ""]
      .map((part) => (part ?? "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return [property.address, property.city, property.state, property.zipCode]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

/**
 * buildZillowLabel returns the user-facing button label for a Zillow link.
 * Vacant land gets "Search Zillow" (signals it may not exist there);
 * residential gets the standard "View on Zillow."
 */
export function buildZillowLabel(property: Property): string {
  return isVacantLand(property) ? "Search Zillow" : "View on Zillow";
}

/**
 * buildRedfinUrl mirrors the Redfin search URL the existing components use.
 * Redfin's search URL handles vacant land + non-residential gracefully on
 * its own (returns "no results" state, not a 404), so no vacant-land
 * special-casing is needed here.
 */
export function buildRedfinUrl(property: Property): string {
  const address = property.address?.trim() ?? "";
  return `https://www.redfin.com/search#query=${encodeURIComponent(address)}`;
}

/**
 * buildPolarisUrl returns the county GIS / parcel-viewer URL for a given
 * county_id, or null when the county has no known equivalent.
 *
 * The frontend should hide the link entirely when this returns null,
 * rather than rendering a broken link. This was the bug surfaced in
 * #238: every county was getting Mecklenburg's polaris3g URL, which
 * 404s or silently resolves to a Mecklenburg neighbor parcel.
 *
 * Long-term (also tracked in #238): per-county URL templates should
 * move into counties.config.gis_url_template (jsonb passthrough) so
 * adding a new county doesn't require a frontend change. This static
 * map is the stop-gap.
 *
 * Also returns null for a synthetic CASE:{court_file_number} parcelId
 * (Brock & Scott / historical Kania rows with no real parcel number —
 * #687 / ADR 0023): county GIS has no such parcel, so the deep link
 * would always 404 rather than merely being unverified.
 */
export function buildPolarisUrl(parcelId: string, countyId?: string): string | null {
  if (isSyntheticParcelId(parcelId)) return null;
  switch (countyId) {
    case "mecklenburg-nc":
    case undefined:
      // Default to Mecklenburg only when no county is provided (legacy
      // call sites that pre-date #238). New call sites must pass the
      // countyId.
      return countyId === undefined
        ? `https://polaris3g.mecklenburgcountync.gov/pid/${parcelId}`
        : `https://polaris3g.mecklenburgcountync.gov/pid/${parcelId}`;
    case "cabarrus-nc":
    case "gaston-nc":
    case "lincoln-nc":
      // No known per-county Polaris equivalent yet — see #238.
      return null;
    default:
      return null;
  }
}

/**
 * buildSpatialEstUrl returns the SpatialEst property-card search URL for
 * a given county_id, or null when SpatialEst doesn't index that county.
 *
 * SpatialEst hosts per-county parcel viewers; the URL pattern follows
 * https://property.spatialest.com/nc/<countySlug>/#/search/?term=<parcel>.
 * Several NC counties are indexed; we only enable links for counties
 * we've manually verified.
 *
 * Also returns null for a synthetic CASE:{court_file_number} parcelId
 * (Brock & Scott / historical Kania rows with no real parcel number —
 * #687 / ADR 0023): SpatialEst has no such parcel to search by.
 */
export function buildSpatialEstUrl(parcelId: string, countyId?: string): string | null {
  if (isSyntheticParcelId(parcelId)) return null;
  switch (countyId) {
    case "mecklenburg-nc":
    case undefined:
      // Mecklenburg runs its own Polaris/SpatialEst instance and is the only
      // county where the SpatialEst property-card link actually resolves by
      // parcel id. (undefined = legacy call sites that pre-date #238.)
      return `https://property.spatialest.com/nc/mecklenburg/#/search/?category=Parcel&page=1&term=${parcelId}`;
    default:
      // Every other county (incl. Cabarrus, which a stale comment claimed was
      // "verified May 2026") is NOT reliably indexed on SpatialEst — the card
      // doesn't resolve by parcel id, so the link dead-ends. Hide it rather
      // than surface a research link that goes nowhere (#581). Re-enable a
      // county here only after manually re-verifying its card resolves.
      return null;
  }
}

/**
 * Street View at the property's coordinates, or null when the row has none.
 * Coordinates only: a street-address search can drop the pin in the wrong city,
 * and the viewpoint link opens the panorama directly. The caller decides who
 * may see it (free viewers get jittered coordinates elsewhere, so this link is
 * shown only for rows the viewer's plan opens in full).
 */
export function buildStreetViewUrl(property: Pick<Property, "latitude" | "longitude">): string | null {
  const lat = property.latitude;
  const lng = property.longitude;
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (!lat && !lng)) return null;
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
}

/** Google Maps pin at the coordinates (satellite view is one click away), or null. */
export function buildGoogleMapsUrl(property: Pick<Property, "latitude" | "longitude">): string | null {
  const lat = property.latitude;
  const lng = property.longitude;
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (!lat && !lng)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}
